import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ProductVariantEditor,
  VariationPreviewRow,
  VariantGroupBucket,
} from '@features/catalog/models/product-form-draft.models';
import {
  ProductVariant,
  ProductVariantBatchMutationResult,
  ProductVariantMutationResult,
} from '@features/catalog/models/product.models';
import {
  buildVariantReconcileUpsertInput,
  buildVariantUpsertInput,
} from '@features/catalog/utils/product-bundle-input.builder';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, finalize, map } from 'rxjs/operators';
import { CatalogProductService } from './catalog-product.service';

export const INLINE_VARIANT_SAVE_DEBOUNCE_MS = 1500;

export interface VariantSavingGate {
  isSaving: (variantId: string) => boolean;
  markSaving: (variantId: string, saving: boolean) => void;
}

export interface QueueVariantSaveInput {
  productId: string | null;
  variantId: string;
  getVariant: () => ProductVariantEditor | undefined;
  saving: VariantSavingGate;
}

export interface FlushPendingVariantSavesInput {
  productId: string;
  getVariants: () => ProductVariantEditor[];
  saving: VariantSavingGate;
  knownAttributeValueIds?: ReadonlySet<string>;
}

export interface FlushPendingVariantSavesResult {
  kind: 'noop' | 'invalid-name' | 'saved' | 'error';
  variantIds: string[];
  savedVariants: ProductVariant[];
  errors: string[];
  invalidVariantId?: string;
  errorMessage?: string;
}

export interface ReconcileVariationsLocalInput {
  currentVariants: ProductVariantEditor[];
  missingRows: VariationPreviewRow[];
  obsoleteVariants: ProductVariantEditor[];
  expectedTotal: number;
  createTempId: () => string;
  defaultPrice?: number;
}

export interface ReconcileVariationsLocalResult {
  kind: 'local';
  variants: ProductVariantEditor[];
  createdCount: number;
  removedCount: number;
  expectedTotal: number;
}

export interface ReconcileVariationsPersistedInput {
  productId: string;
  currentVariants: ProductVariantEditor[];
  missingRows: VariationPreviewRow[];
  obsoleteVariants: ProductVariantEditor[];
  expectedTotal: number;
  knownAttributeValueIds: ReadonlySet<string>;
  defaultPrice?: number;
}

export interface ReconcileVariationsPersistedResult {
  kind: 'persisted';
  removedIds: string[];
  createdVariants: ProductVariant[];
  errors: string[];
  expectedTotal: number;
  removedCount: number;
  createdCount: number;
}

export type ReconcileVariationsResult =
  | ReconcileVariationsLocalResult
  | Observable<ReconcileVariationsPersistedResult>;

export interface SaveVariantInput {
  productId: string;
  variant: ProductVariantEditor;
  saving: VariantSavingGate;
}

export interface SaveVariantResult {
  kind: 'saved' | 'error';
  savedVariant?: ProductVariant;
  errors: string[];
  errorMessage?: string;
}

export interface RemovePersistedVariantInput {
  variantId: string;
  saving: VariantSavingGate;
}

export interface RemovePersistedVariantResult {
  kind: 'removed' | 'validation-error' | 'error';
  errors: string[];
  errorMessage?: string;
}

export interface RemoveVariantGroupInput {
  productId: string | null;
  group: VariantGroupBucket;
  saving: VariantSavingGate;
}

export interface RemoveVariantGroupLocalResult {
  kind: 'local';
  removedIds: string[];
}

export interface RemoveVariantGroupPersistedResult {
  kind: 'persisted';
  removedIds: string[];
  errors: string[];
  shouldPersistDraft: boolean;
  errorMessage?: string;
}

export type RemoveVariantGroupResult =
  | RemoveVariantGroupLocalResult
  | Observable<RemoveVariantGroupPersistedResult>;

@Injectable()
export class ProductFormVariantOrchestrationService {
  private readonly inlineVariantSaveRequests = new Subject<void>();
  private readonly pendingInlineVariantIds = new Set<string>();

  constructor(private readonly productService: CatalogProductService) {}

  setupInlineSaveDebounce(destroyRef: DestroyRef, onFlush: () => void): void {
    destroyRef.onDestroy(() => this.clearPendingVariantSaves());
    this.inlineVariantSaveRequests
      .pipe(debounceTime(INLINE_VARIANT_SAVE_DEBOUNCE_MS), takeUntilDestroyed(destroyRef))
      .subscribe(() => onFlush());
  }

  queueVariantSave(input: QueueVariantSaveInput): void {
    if (!input.productId) {
      return;
    }

    const variant = input.getVariant();
    if (!variant || variant.isNew || input.saving.isSaving(input.variantId)) {
      return;
    }

    if (variant.price < 0) {
      return;
    }

    this.pendingInlineVariantIds.add(input.variantId);
    this.inlineVariantSaveRequests.next();
  }

  requestVariantInlineSave(variantId: string, input: Omit<QueueVariantSaveInput, 'variantId'>): void {
    this.queueVariantSave({ ...input, variantId });
  }

  clearPendingVariantSaves(): void {
    this.pendingInlineVariantIds.clear();
  }

  flushPendingVariantSaves(input: FlushPendingVariantSavesInput): Observable<FlushPendingVariantSavesResult> {
    if (!input.productId || this.pendingInlineVariantIds.size === 0) {
      return of({ kind: 'noop', variantIds: [], savedVariants: [], errors: [] });
    }

    const idsToSave = Array.from(this.pendingInlineVariantIds).filter((id) => {
      const variant = input.getVariants().find((item) => item.id === id);
      if (!variant || variant.isNew || input.saving.isSaving(id)) {
        return false;
      }
      return variant.price >= 0;
    });

    if (!idsToSave.length) {
      return of({ kind: 'noop', variantIds: [], savedVariants: [], errors: [] });
    }

    const variantsToSave = idsToSave
      .map((id) => input.getVariants().find((item) => item.id === id) ?? null)
      .filter((variant): variant is ProductVariantEditor => variant != null);

    const invalidVariant = variantsToSave.find((variant) => !variant.name.trim());
    if (invalidVariant) {
      this.pendingInlineVariantIds.delete(invalidVariant.id);
      return of({
        kind: 'invalid-name',
        variantIds: idsToSave,
        savedVariants: [],
        errors: [],
        invalidVariantId: invalidVariant.id,
      });
    }

    idsToSave.forEach((id) => {
      this.pendingInlineVariantIds.delete(id);
      input.saving.markSaving(id, true);
    });

    return this.productService
      .upsertVariants({
        productId: input.productId,
        variants: variantsToSave.map((variant) =>
          buildVariantUpsertInput(variant, input.knownAttributeValueIds)
        ),
      })
      .pipe(
        map((result) => {
          idsToSave.forEach((id) => input.saving.markSaving(id, false));
          return {
            kind: 'saved' as const,
            variantIds: idsToSave,
            savedVariants: result.variants ?? [],
            errors: result.errors ?? [],
          };
        }),
        catchError((err: Error) => {
          idsToSave.forEach((id) => input.saving.markSaving(id, false));
          this.requeuePendingVariantSaves(idsToSave);
          return of({
            kind: 'error' as const,
            variantIds: idsToSave,
            savedVariants: [],
            errors: [],
            errorMessage: err.message,
          });
        }),
        finalize(() => {
          idsToSave.forEach((id) => input.saving.markSaving(id, false));
        })
      );
  }

  requeuePendingVariantSaves(variantIds: string[]): void {
    variantIds.forEach((id) => this.pendingInlineVariantIds.add(id));
  }

  reconcileVariations(input: ReconcileVariationsLocalInput): ReconcileVariationsLocalResult;
  reconcileVariations(input: ReconcileVariationsPersistedInput): Observable<ReconcileVariationsPersistedResult>;
  reconcileVariations(
    input: ReconcileVariationsLocalInput | ReconcileVariationsPersistedInput
  ): ReconcileVariationsResult {
    if (!('productId' in input)) {
      return this.reconcileVariationsLocally(input);
    }

    return this.reconcileVariationsPersisted(input);
  }

  saveVariant(input: SaveVariantInput): Observable<SaveVariantResult> {
    input.saving.markSaving(input.variant.id, true);

    return this.productService
      .upsertVariants({
        productId: input.productId,
        variants: [buildVariantUpsertInput(input.variant)],
      })
      .pipe(
        map((result) => {
          if (result.errors?.length) {
            return {
              kind: 'error' as const,
              errors: result.errors,
            };
          }

          const savedVariant = result.variants[0];
          if (!savedVariant) {
            return {
              kind: 'error' as const,
              errors: ['missing-variant'],
            };
          }

          return {
            kind: 'saved' as const,
            savedVariant,
            errors: [],
          };
        }),
        catchError((err: Error) =>
          of({
            kind: 'error' as const,
            errors: [],
            errorMessage: err.message,
          })
        ),
        finalize(() => input.saving.markSaving(input.variant.id, false))
      );
  }

  removePersistedVariant(input: RemovePersistedVariantInput): Observable<RemovePersistedVariantResult> {
    input.saving.markSaving(input.variantId, true);

    return this.productService.destroyVariant(input.variantId).pipe(
      map((result: ProductVariantMutationResult) => {
        if (result.errors.length) {
          return {
            kind: 'validation-error' as const,
            errors: result.errors,
          };
        }

        return {
          kind: 'removed' as const,
          errors: [],
        };
      }),
      catchError((err: Error) =>
        of({
          kind: 'error' as const,
          errors: [],
          errorMessage: err.message,
        })
      ),
      finalize(() => input.saving.markSaving(input.variantId, false))
    );
  }

  removeVariantGroup(input: RemoveVariantGroupInput): RemoveVariantGroupResult {
    const groupVariants = [...input.group.variants];

    if (!input.productId) {
      return {
        kind: 'local',
        removedIds: groupVariants.map((variant) => variant.id),
      };
    }

    const localOnly = groupVariants.filter((variant) => variant.isNew || !variant.id.trim());
    const persisted = groupVariants.filter((variant) => !variant.isNew && variant.id.trim().length > 0);

    if (!persisted.length) {
      return {
        kind: 'local',
        removedIds: localOnly.map((variant) => variant.id),
      };
    }

    for (const variant of persisted) {
      input.saving.markSaving(variant.id, true);
    }

    return forkJoin(persisted.map((variant) => this.productService.destroyVariant(variant.id))).pipe(
      map((results) => {
        const errors = results.flatMap((result) => result.errors);
        const deletedPersistedIds = persisted
          .filter((_, index) => !(results[index]?.errors?.length ?? 0))
          .map((variant) => variant.id);

        const removedIds = [...localOnly.map((variant) => variant.id), ...deletedPersistedIds];

        for (const variant of persisted) {
          input.saving.markSaving(variant.id, false);
        }

        return {
          kind: 'persisted' as const,
          removedIds,
          errors,
          shouldPersistDraft: localOnly.length > 0,
        };
      }),
      finalize(() => {
        for (const variant of persisted) {
          input.saving.markSaving(variant.id, false);
        }
      })
    );
  }

  private reconcileVariationsLocally(
    input: ReconcileVariationsLocalInput
  ): ReconcileVariationsLocalResult {
    const obsoleteIds = new Set(input.obsoleteVariants.map((variant) => variant.id));
    const keptVariants = input.currentVariants.filter((variant) => !obsoleteIds.has(variant.id));
    const basePosition = keptVariants.length;
    const defaultPrice = Number(input.defaultPrice ?? 0);
    const localRows: ProductVariantEditor[] = input.missingRows.map((row, index) => ({
      id: `tmp_${input.createTempId()}`,
      isNew: true,
      name: row.name,
      sku: '',
      externalReference: '',
      imageUrl: null,
      cost: null,
      price: defaultPrice,
      stockQuantity: null,
      position: basePosition + index,
      active: true,
      attributeValues: row.attributeValues,
      attributesText: row.attributesText,
    }));

    return {
      kind: 'local',
      variants: [...keptVariants, ...localRows],
      createdCount: localRows.length,
      removedCount: input.obsoleteVariants.length,
      expectedTotal: input.expectedTotal,
    };
  }

  private reconcileVariationsPersisted(
    input: ReconcileVariationsPersistedInput
  ): Observable<ReconcileVariationsPersistedResult> {
    const obsoleteNewIds = new Set(
      input.obsoleteVariants.filter((variant) => variant.isNew).map((variant) => variant.id)
    );
    const obsoletePersisted = input.obsoleteVariants.filter(
      (variant) => !variant.isNew && variant.id.trim().length > 0
    );

    const deleteRequests = obsoletePersisted.map((variant) =>
      this.productService.destroyVariant(variant.id)
    );

    const currentCountAfterRemoval = input.currentVariants.length - input.obsoleteVariants.length;
    const upsertRequest: Observable<ProductVariantBatchMutationResult> = input.missingRows.length
      ? this.productService.upsertVariants({
          productId: input.productId,
          variants: input.missingRows.map((row, index) => ({
            name: row.name,
            price: Number(input.defaultPrice ?? 0),
            position: currentCountAfterRemoval + index,
            active: true,
            ...buildVariantReconcileUpsertInput(row, input.knownAttributeValueIds),
          })),
        })
      : of({ variants: [], variantIds: [], errors: [] });

    return forkJoin([
      deleteRequests.length ? forkJoin(deleteRequests) : of([]),
      upsertRequest,
    ]).pipe(
      map(([deleteResults, upsertResult]) => {
        const deleteErrors = deleteResults.flatMap((result) => result.errors ?? []);
        const createErrors = upsertResult.errors ?? [];

        const deletedIds = obsoletePersisted
          .filter((_, index) => !(deleteResults[index]?.errors?.length ?? 0))
          .map((variant) => variant.id);

        const removedIds = [...obsoleteNewIds, ...deletedIds];

        return {
          kind: 'persisted' as const,
          removedIds,
          createdVariants: upsertResult.variants ?? [],
          errors: [...deleteErrors, ...createErrors],
          expectedTotal: input.expectedTotal,
          removedCount: removedIds.length,
          createdCount: (upsertResult.variants ?? []).length,
        };
      })
    );
  }
}
